// ═══════════════════════════════════════════════════════════════════════════
// Entrar con un ticket, sin correo y sin contraseña
//
// Es la otra mitad del QR. Hasta 0.34.0 el código llevaba a la pantalla de
// canje, que exige sesión previa: el invitado tenía que crearse una cuenta con
// correo. Con esto, apuntar la cámara alcanza.
//
// El camino es el mismo que el de la llave de acceso, y por la misma razón: la
// función de borde valida el ticket y le PIDE a Supabase un token de un solo
// uso; el navegador lo cambia por la sesión con `verifyOtp`. No fabricamos
// tokens — si lo hiciéramos, tendríamos una sesión que Supabase no reconoce y
// la RLS no sabría quién eres.
//
// La sesión que queda es permanente hasta que alguien la revoque, que es lo
// acordado para el vendedor: no hay "olvidé mi contraseña", hay "pídele otro
// ticket a tu jefe".
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from "./sesion.js";
import { configSupabase } from "./config.js";
import { olvidarTicket } from "./ticket.js";

/**
 * El sufijo del correo interno que se le pone a quien entra por ticket. No es
 * de nadie: el TLD `.invalid` está reservado por el RFC 2606 justamente para
 * eso, así que ningún correo puede salir hacia allá ni por accidente.
 */
const DOMINIO_DISPOSITIVO = /@dispositivos\.invalid$/i;

/**
 * ¿Este "correo" es en realidad un aparato?
 *
 * Vive aquí y no en la pantalla del equipo porque no es una decisión de
 * presentación: es saber si detrás de una cuenta hay una persona con correo o
 * un teléfono que entró con un ticket. La lista del equipo lo usa para no
 * enseñar cuarenta caracteres de UUID donde debería ir un nombre.
 */
export const esDispositivo = (correo) =>
  DOMINIO_DISPOSITIVO.test(String(correo ?? ""));

/**
 * Un nombre para el aparato, para que quien administre pueda distinguirlos.
 *
 * Sale del `userAgent` porque preguntárselo a alguien que apenas está entrando
 * es una fricción que nadie va a agradecer, y porque un nombre aproximado
 * —"Android · Chrome"— ya sirve para lo único que hace falta: saber cuál
 * revocar cuando se pierda un teléfono. Se puede cambiar después.
 */
export function nombreDelDispositivo() {
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const sistema =
    /iPhone|iPad|iPod/i.test(ua) ? "iPhone" :
    /Android/i.test(ua)          ? "Android" :
    /Macintosh/i.test(ua)        ? "Mac" :
    /Windows/i.test(ua)          ? "Windows" :
    /Linux/i.test(ua)            ? "Linux" : "Dispositivo";
  const navegador =
    /Edg\//i.test(ua)     ? "Edge" :
    /Chrome\//i.test(ua)  ? "Chrome" :
    /Firefox\//i.test(ua) ? "Firefox" :
    /Safari\//i.test(ua)  ? "Safari" : null;
  return navegador ? `${sistema} · ${navegador}` : sistema;
}

/**
 * Canjea el ticket y deja la sesión abierta. Devuelve a qué vinícola entró.
 *
 * Los errores del ticket se dejan pasar TAL CUAL vienen del servidor —"ya se
 * usó", "venció el 3 de agosto"— porque quien está parado frente al QR necesita
 * saber si tiene que pedir otro. Traducirlos a un "no se pudo" genérico lo
 * mandaría a adivinar.
 */
export async function entrarConTicket(codigo, etiqueta) {
  const { url, anonKey } = configSupabase();

  const r = await fetch(`${url}/functions/v1/entrar-con-ticket`, {
    method: "POST",
    headers: { "Authorization": `Bearer ${anonKey}`, "Content-Type": "application/json" },
    body: JSON.stringify({
      codigo: String(codigo || "").trim().toUpperCase(),
      etiqueta: etiqueta || nombreDelDispositivo(),
    }),
  });

  let cuerpo = null;
  try { cuerpo = await r.json(); } catch { /* respuesta sin JSON */ }

  if (!r.ok) {
    // ── Un ticket muerto se OLVIDA ─────────────────────────────────────────
    //
    // El código se guarda en `localStorage` para sobrevivir el ida y vuelta del
    // correo de confirmación (ver `ticket.js`). Pero si el servidor dice que ya
    // se usó, que venció o que no existe, guardarlo es dejar a la persona en un
    // bucle: cada vez que abre la app le sale la pantalla de invitación con un
    // código muerto y ningún camino que no sea borrar los datos del navegador.
    // El operador se topó con eso.
    //
    // Se distingue por el CÓDIGO DE ESTADO y no por el texto del mensaje: 400
    // es un código con mala forma y 409 es el rechazo del canje —usado,
    // vencido, revocado, reservado a otro correo—. Un 500 o un fallo de red no
    // dicen nada del ticket, así que ese se conserva.
    if (r.status === 400 || r.status === 409) olvidarTicket();
    throw new Error(cuerpo?.error || "No se pudo entrar con ese ticket.");
  }

  const { data, error } = await supabase.auth.verifyOtp({
    token_hash: cuerpo.token_hash, type: "magiclink",
  });
  if (error) throw new Error(error.message);

  // Ya se usó: no debe reaparecer. Faltaba, y por eso la pantalla de invitación
  // volvía a salir en cada visita con un código gastado.
  olvidarTicket();

  return { sesion: data.session, tenant: cuerpo.tenant, role: cuerpo.role };
}
